import { EngagementConferenceParticipant } from '../slices/marketplace/meetings/models/engagement-conference-participant';
import { EngagementMeetingParticipant } from '../slices/marketplace/meetings/models/engagement-meeting-participant';
import { MeetingFormat } from '../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { EngagementMeetingStatus } from '../slices/marketplace/meetings/models/enums/meeting-status.enum';
import { TimelineParty } from '../slices/marketplace/timeline/models/enums/timeline-party.enum';

export interface ParticipantEntry {
    name: string;
    /**
     * Which source list the entry came from (roster vs attendance), not the
     * participant's actual attendance mode — see
     * EngagementMeetingParticipant.attendanceMode for the per-person value.
     */
    isInPerson: boolean;
}

export interface ParticipantColumns {
    seller: ParticipantEntry[];
    buyer: ParticipantEntry[];
}

export interface MeetingParticipantSources {
    format: MeetingFormat;
    status: EngagementMeetingStatus;
    participants?: EngagementConferenceParticipant[];
    meetingParticipants?: EngagementMeetingParticipant[];
}

export const buildParticipantColumns = (
    inPersonList: EngagementMeetingParticipant[],
    virtualList: EngagementConferenceParticipant[]
): ParticipantColumns => {
    const rawSeller: ParticipantEntry[] = [];
    const rawBuyer: ParticipantEntry[] = [];

    inPersonList.forEach((p) => {
        if (p.party === TimelineParty.Seller) rawSeller.push({ name: p.name, isInPerson: true });
        else if (p.party === TimelineParty.Buyer) rawBuyer.push({ name: p.name, isInPerson: true });
    });

    virtualList.forEach((p) => {
        if (p.engagementRole === TimelineParty.Seller) rawSeller.push({ name: p.name, isInPerson: false });
        else if (p.engagementRole === TimelineParty.Buyer) rawBuyer.push({ name: p.name, isInPerson: false });
    });

    const dedupByName = (list: ParticipantEntry[]): ParticipantEntry[] => {
        const seen = new Set<string>();
        return [
            ...list.filter((p) => p.isInPerson),
            ...list.filter((p) => !p.isInPerson),
        ].filter((p) => {
            const key = p.name.toLowerCase().trim();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    return { seller: dedupByName(rawSeller), buyer: dedupByName(rawBuyer) };
};

export const getMeetingParticipantColumns = (
    meeting: MeetingParticipantSources
): ParticipantColumns => {
    const roster = meeting.meetingParticipants || [];
    const attendance = meeting.participants || [];

    if (meeting.format === MeetingFormat.InPerson || meeting.format === MeetingFormat.Mixed) {
        return buildParticipantColumns(roster, []);
    }

    if (meeting.status === EngagementMeetingStatus.Completed) {
        const attended = buildParticipantColumns([], attendance);
        const merged = buildParticipantColumns(roster, attendance);
        return {
            seller: attended.seller.length > 0 ? merged.seller : [],
            buyer: attended.buyer.length > 0 ? merged.buyer : [],
        };
    }

    if (roster.length > 0) {
        return buildParticipantColumns(roster, []);
    }

    return buildParticipantColumns([], attendance);
};
